import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, useMediaQuery } from "@mui/material";
import {
  ArrowBackOutlined,
  PrecisionManufacturingOutlined,
  RotateRightOutlined,
  AutoFixHighOutlined,
  BoltOutlined,
  FlareOutlined,
  Inventory2Outlined,
} from "@mui/icons-material";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { getCatalogModelDetail, clearSelectedModel } from "../machineCatalogSlice";
import { CatalogModelDetail, CatalogModelListItem, CatalogSpecSection, MachineCatalogState } from "../models/models";
import MachineSpecs from "./MachineSpecs";
import MachineDialog from "./MachineDialog";
import { formatManufacturerWithHierarchy } from "../../../shared/utils/machineUtils";
import useTranslation from "../../../shared/hooks/useTranslation";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";
import { ImageGallery, useGalleryImages } from "../../../shared/components/gallery";
import CatalogReadinessRow from "./CatalogReadinessRow";
import MidasReadinessPopup from "./readiness/MidasReadinessPopup";

// =====================================================================
//  Category design tokens
// =====================================================================

const categoryColorMap: Record<string, string> = {
  "machining-centers": dt.blue,
  "turning-machines": "#8E44AD",
  "grinding-machines": "#E67E22",
  "edm": "#27AE60",
  "laser-machines": "#E74C3C",
  "packaging-lines": "#795548",
};

const categoryLabelMap: Record<string, string> = {
  "machining-centers": "Machining Center",
  "turning-machines": "Turning Machine",
  "grinding-machines": "Grinding Machine",
  "edm": "EDM",
  "laser-machines": "Laser Machine",
  "packaging-lines": "Packaging Line",
};

const categoryIconMap: Record<string, React.ReactElement> = {
  "machining-centers": <PrecisionManufacturingOutlined />,
  "turning-machines": <RotateRightOutlined />,
  "grinding-machines": <AutoFixHighOutlined />,
  "edm": <BoltOutlined />,
  "laser-machines": <FlareOutlined />,
  "packaging-lines": <Inventory2Outlined />,
};

const platformCountDt = {
  bg: "rgba(39, 174, 96, 0.08)",
  text: "#27AE60",
  border: "rgba(39, 174, 96, 0.15)",
};

// =====================================================================
//  Key Specs helper -- extract up to 6 important specs from sections
// =====================================================================

interface KeySpec {
  label: string;
  value: string;
}

const KEY_SPEC_KEYS = [
  "Max Spindle Speed",
  "Spindle Speed",
  "Spindle Power",
  "HP",
  "Max HP",
  "Taper Size",
  "Table Size",
  "Table Length",
  "# of Tools",
  "Number of Tools",
  "Tool Capacity",
  "Travels (X)",
  "X-Axis Travel",
  "Chuck Size",
  "Max Turning Diameter",
  "Max Turning Length",
  "Wheel Diameter",
  "Grinding Length",
];

function extractKeySpecs(sections: CatalogSpecSection[]): KeySpec[] {
  const found: KeySpec[] = [];
  const allSpecs = sections.flatMap((s) => s.specs);
  for (const key of KEY_SPEC_KEYS) {
    if (found.length >= 6) break;
    const spec = allSpecs.find(
      (s) => s.specKey.toLowerCase().includes(key.toLowerCase())
    );
    if (spec) {
      const value = spec.unit ? `${spec.specValue} ${spec.unit}` : spec.specValue;
      found.push({ label: spec.specKey, value });
    }
  }
  return found;
}

// =====================================================================
//  MAIN COMPONENT
// =====================================================================

const CatalogModelDetailPage: React.FC = () => {
  useRedesignSetup();

  const { modelId } = useParams<{ modelId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");

  const isCompact = useMediaQuery("(max-width:1200px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  const { selectedModel, selectedModelLoading } = useAppSelector<MachineCatalogState>(
    (state: any) => state.machineCatalog
  );

  const [showMachineDialog, setShowMachineDialog] = useState(false);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (modelId) {
      hasFetchedRef.current = true;
      dispatch(getCatalogModelDetail(modelId));
    }
    return () => {
      dispatch(clearSelectedModel());
    };
  }, [modelId, dispatch]);

  const model = selectedModel;
  const categoryColor = model ? (categoryColorMap[model.categoryCode] || dt.blue) : dt.blue;
  const categoryLabel = model ? (categoryLabelMap[model.categoryCode] || model.categoryCode) : "";
  const unitsOnPlatform = model ? ((model as any).unitsOnPlatform ?? 0) : 0;

  const keySpecs = useMemo(() => {
    if (!model?.specSections) return [];
    return extractKeySpecs(model.specSections);
  }, [model]);

  // Build gallery images. The model row's own image (CatalogMachineModels.ImagePath,
  // surfaced as imageDetailUrl/imageStandardUrl) is the authoritative hero — it is what
  // the catalog browse cards render and what catalog image curation updates.
  // model.images (CatalogModelImages) carries a denormalised COPY of that hero
  // (imageType === "hero") plus the real extra angle/detail shots; when the model row is
  // repointed without rewriting the copy the two disagree, and preferring the copy made
  // this page render a stale photo while the card rendered the correct one. So: hero from
  // the model row, extras only from the non-hero rows. Same rule as useGalleryImages —
  // see the comment there for the measured blast radius.
  const catalogGalleryUrls = useMemo(() => {
    const heroUrl = model?.imageDetailUrl || model?.imageStandardUrl || model?.imageUrl || null;
    const urls: string[] = heroUrl ? [heroUrl] : [];

    [...(model?.images || [])]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .forEach(img => {
        if (heroUrl && img.imageType === "hero") return;
        const url = img.imageDetailUrl || img.imageStandardUrl;
        if (!url || urls.includes(url)) return;
        urls.push(url);
      });

    return urls;
  }, [model]);

  const catalogGalleryImages = useGalleryImages({
    type: "urls",
    urls: catalogGalleryUrls,
  });

  // Convert CatalogModelDetail to CatalogModelListItem shape for MachineDialog
  const catalogModelForDialog: CatalogModelListItem | undefined = model ? {
    id: model.id,
    techspexId: model.techspexId,
    modelName: model.modelName,
    categoryCode: model.categoryCode,
    subType: model.subType,
    cncBrand: model.cncBrand,
    numAxes: model.numAxes,
    imageUrl: model.imageUrl,
    imageStandardUrl: model.imageStandardUrl,
    imageDetailUrl: model.imageDetailUrl,
    specsCount: model.specSections?.reduce((sum, s) => sum + s.specs.length, 0) ?? 0,
    manufacturer: model.manufacturer,
  } : undefined;

  // --- Loading: show spinner during fetch OR on first render before useEffect fires ---
  if (!model && (selectedModelLoading || !hasFetchedRef.current)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          minHeight: "400px",
          backgroundColor: dt.pageBg,
        }}
      >
        <CircularProgress size={36} sx={{ color: dt.blue }} />
      </Box>
    );
  }

  // --- No model found ---
  if (!model) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          minHeight: "400px",
          backgroundColor: dt.pageBg,
          fontFamily: dt.font,
          gap: 2,
        }}
      >
        <PrecisionManufacturingOutlined sx={{ fontSize: 48, color: dt.textMuted }} />
        <Box sx={{ fontSize: "16px", color: dt.textSecondary }}>
          Model not found
        </Box>
        <Button
          onClick={() => navigate("/machinery/catalog")}
          sx={{
            textTransform: "none",
            fontFamily: dt.font,
            color: dt.blue,
          }}
        >
          {translate("backToCatalog")}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        fontFamily: dt.font,
        overflow: "auto",
        px: { xs: "16px", md: "24px" },
        pb: 4,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
      }}
    >
      {/* Back link + page title */}
      <Box
        sx={{
          pt: { xs: 2, md: 3 },
          pb: 1,
          animation: "headerReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
      >
        <Box
          onClick={() => navigate("/machinery/catalog")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            mb: 1,
          }}
        >
          <ArrowBackOutlined sx={{ fontSize: 18, color: dt.textSecondary }} />
          <Box
            sx={{
              fontSize: "13px",
              color: dt.textLink,
              fontFamily: dt.font,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {translate("backToCatalog")}
          </Box>
        </Box>

        <Box
          sx={{
            fontSize: isPhone ? "20px" : isCompact ? "22px" : "28px",
            fontWeight: 800,
            fontFamily: dt.font,
            color: dt.textPrimary,
            letterSpacing: "-0.5px",
          }}
        >
          {translate("machineCatalog")}
        </Box>
        <Box
          sx={{
            width: "40px",
            height: "3px",
            background: `linear-gradient(90deg, ${dt.blue}, transparent)`,
            borderRadius: "2px",
            mt: 0.5,
          }}
        />
      </Box>

      {/* Main content: 40/60 split on desktop, stacked on phone */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isCompact ? "column" : "row",
          gap: isCompact ? "24px" : "32px",
          mt: 2,
          animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
        }}
      >
        {/* Left panel: Image + Info */}
        <Box
          sx={{
            width: isCompact ? "100%" : "40%",
            minWidth: isCompact ? "auto" : "340px",
            flexShrink: 0,
          }}
        >
          {/* Image gallery (carousel when multiple images) */}
          {catalogGalleryImages.length > 0 ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: "480px",
                borderRadius: dt.radius,
                overflow: "hidden",
              }}
            >
              <ImageGallery
                images={catalogGalleryImages}
                variant="detail"
                showThumbnails={catalogGalleryImages.length > 1}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                maxWidth: "480px",
                height: isPhone ? "200px" : "300px",
                borderRadius: dt.radius,
                border: `1px solid ${dt.cardBorder}`,
                backgroundColor: `${categoryColor}06`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {React.cloneElement(
                categoryIconMap[model.categoryCode] || <PrecisionManufacturingOutlined />,
                { sx: { fontSize: 48, color: `${categoryColor}60` } }
              )}
            </Box>
          )}

          {/* Model name */}
          <Box
            sx={{
              fontSize: isPhone ? "20px" : "24px",
              fontWeight: 700,
              fontFamily: dt.font,
              color: dt.textPrimary,
              letterSpacing: "-0.3px",
              mt: 2,
            }}
          >
            {model.modelName}
          </Box>

          {/* Manufacturer name */}
          <Box
            sx={{
              fontSize: "15px",
              fontWeight: 400,
              fontFamily: dt.font,
              color: dt.textSecondary,
              mt: 0.5,
            }}
          >
            {model.manufacturer
              ? formatManufacturerWithHierarchy(
                  model.manufacturer.name,
                  model.manufacturer.parentManufacturerName
                )
              : ""}
          </Box>

          {/* Category + Type badges */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
            {/* Category badge */}
            <Box
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                fontFamily: dt.font,
                color: categoryColor,
                backgroundColor: `${categoryColor}10`,
                borderRadius: "6px",
                padding: "4px 10px",
              }}
            >
              {categoryLabel}
            </Box>

            {/* Axis badge */}
            {model.numAxes && (
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: dt.font,
                  color: dt.textSecondary,
                  backgroundColor: dt.inputBg,
                  borderRadius: "6px",
                  padding: "4px 10px",
                }}
              >
                {model.numAxes}-axis
              </Box>
            )}

            {/* CNC brand badge */}
            {model.cncBrand && (
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: dt.font,
                  color: dt.textSecondary,
                  backgroundColor: dt.inputBg,
                  borderRadius: "6px",
                  padding: "4px 10px",
                }}
              >
                {model.cncBrand}
              </Box>
            )}
          </Box>

          {/* Key specs summary card */}
          {keySpecs.length > 0 && (
            <Box
              sx={{
                background: dt.headerBg,
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`,
                padding: "16px",
                mt: 2,
              }}
            >
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: dt.textMuted,
                  fontFamily: dt.font,
                  mb: 1.5,
                }}
              >
                {translate("keySpecs")}
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px 24px",
                }}
              >
                {keySpecs.map((spec) => (
                  <Box key={spec.label}>
                    <Box
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: dt.textMuted,
                        fontFamily: dt.font,
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                      }}
                    >
                      {spec.label}
                    </Box>
                    <Box
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: dt.textPrimary,
                        fontFamily: dt.fontMono,
                        mt: 0.25,
                      }}
                    >
                      {spec.value}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Platform count */}
          <Box
            sx={{
              mt: 2,
              fontSize: "13px",
              fontFamily: dt.fontMono,
              fontWeight: 600,
              color: unitsOnPlatform > 0 ? platformCountDt.text : dt.textMuted,
            }}
          >
            {unitsOnPlatform > 0
              ? translate("unitsOnPlatform", { count: unitsOnPlatform })
              : translate("noUnitsYet")}
          </Box>

          {/* DR-3 (item 5): MIDAS Data Readiness — model-level (no live status on
              the catalog). Same dot-row + "View details ›" treatment as the
              catalog card, opening the shared self-configuring popup. */}
          <Box
            sx={{
              mt: 2,
              backgroundColor: dt.cardBg,
              border: `1px solid ${dt.cardBorder}`,
              borderRadius: dt.radiusSm,
              padding: "14px 16px",
            }}
          >
            <Box
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "1px",
                color: dt.textMuted,
                fontFamily: dt.font,
                mb: 1,
              }}
            >
              {translate("midasDataReadiness")}
            </Box>
            <CatalogReadinessRow model={{ readiness: model.readiness, isOemPartner: model.isOemPartner }} />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "8px" }}>
              <Box
                onClick={() => setReadinessOpen(true)}
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  fontFamily: dt.font,
                  color: dt.textLink,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {translate("viewDetails")} ›
              </Box>
            </Box>
          </Box>

          <MidasReadinessPopup
            open={readinessOpen}
            onClose={() => setReadinessOpen(false)}
            readiness={model.readiness}
            isOemPartner={model.isOemPartner}
            oemPartnerName={model.oemPartnerName}
            machineOrModelName={model.modelName}
            manufacturerName={model.manufacturer?.name}
          />

          {/* "Add This Machine" button */}
          <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
            <Button
              variant="contained"
              onClick={() => setShowMachineDialog(true)}
              sx={{
                background: accentGradientDark,
                borderRadius: dt.radiusPill,
                padding: "12px 30px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                fontFamily: dt.font,
                boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
                color: "#FFFFFF",
                width: isPhone ? "100%" : "auto",
                mt: 2,
                "&:hover": {
                  background: accentGradientDarkHover,
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(52,152,219,0.3)",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {translate("addThisMachine")}
            </Button>
          </PermissionGuard>
        </Box>

        {/* Right panel: Specifications */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontSize: isPhone ? "18px" : "20px",
              fontWeight: 700,
              fontFamily: dt.font,
              color: dt.textPrimary,
              mb: 0.5,
            }}
          >
            {translate("specifications")}
          </Box>
          <Box
            sx={{
              width: "30px",
              height: "3px",
              background: `linear-gradient(90deg, ${categoryColor}, transparent)`,
              borderRadius: "2px",
              mb: 2,
            }}
          />

          {/* Reuse existing MachineSpecs component */}
          <MachineSpecs catalogModelId={modelId!} />
        </Box>
      </Box>

      {/* MachineDialog -- opens as "Add new" with catalog model pre-selected */}
      <MachineDialog
        key={`catalog-detail-dialog-${showMachineDialog}`}
        open={showMachineDialog}
        onClose={() => setShowMachineDialog(false)}
        isNew={true}
        preSelectedCatalogModel={catalogModelForDialog}
        onCreated={(id) => navigate(id ? `/machinery?newMachine=${id}` : "/machinery")}
      />
    </Box>
  );
};

export default CatalogModelDetailPage;
